/**
 * E-GTCC-VIEW-DPLANE1 — read client of the GTCC view "Data Plane".
 *
 * The Owner order for this view is "1:1 nach Referenz" and "vorhandene
 * Module messen und wiederverwenden". Both are honoured literally: this
 * module adds no endpoint and no second client. It is a thin adapter over
 * the three clients that already serve the old console —
 *
 *   `apps/admin/src/data-plane/api.ts`    (Health Center: placement,
 *                                          backups, retention, VPN)
 *   `apps/admin/src/db-ops/api.ts`        (Control Plane inventory,
 *                                          plan builders, plan history)
 *   `apps/admin/src/monitoring/api.ts`    (Monitoring & Alerts (DB))
 *
 * — and it does exactly two things they do not: it maps every failure onto
 * one discriminated union so no tab ever sees an `ApiError`, and it owns
 * the `["gtcc", "dplane", …]` query keys of the GTCC key inventory.
 *
 * Read-only by construction. The only non-GET calls re-exported here are
 * the three plan *dry runs* (`mode: "dry_run"`, server-side), and they are
 * reachable only from an explicit [Validate] click. No approval verb, no
 * runner verb, no execution verb is imported — execution stays on the old
 * console behind Owner-Go.
 *
 * No value is computed here. Statuses, ages and verdicts arrive decided by
 * the API (Owner decisions P8/P9/P10: a stale source is never drawn as
 * healthy, and an unmeasurable value reads "nicht messbar" instead of an
 * invented number).
 */
import { fetchDataPlaneOverview } from "../../data-plane/api.js";
import type { DataPlaneHealthOverview } from "../../data-plane/types.js";
import {
  fetchDbOpsDatabases,
  fetchDbOpsHosts,
  fetchDbOpsOverview,
  fetchDbOpsPersistedPlans,
  fetchDbOpsPlanAuditEvents,
  fetchDbOpsPostgresInstances,
  fetchDbOpsTenantAssignments,
  submitAssignmentPlan,
  submitCreateDatabasePlan,
  submitMovePlan,
  type AssignmentPlanRequest,
  type CreateDatabasePlanRequest,
  type DbOpsPersistedAuditEvent,
  type DbOpsPersistedPlan,
  type MovePlanRequest,
} from "../../db-ops/api.js";
import type {
  DbOpsDatabase,
  DbOpsHost,
  DbOpsOverview,
  PostgresInstance,
  TenantDatabaseAssignment,
} from "../../db-ops/types.js";
import { ApiError } from "../../lib/api.js";
import {
  fetchMonitoringSummary,
  fetchVpsFleet,
  type MonitoringSummaryResult,
  type VpsFleetResult,
} from "../../monitoring/api.js";
import type { MonitoringSummaryDto, VpsFleetDto } from "../../monitoring/types.js";

/* --------------------------------------------------------------- paths */

/**
 * The paths are not re-declared; they belong to the clients above. They are
 * named here only so the endpoint inventory and the read-only guard can
 * anchor on this module instead of on three unrelated ones.
 */
export const DPLANE_OVERVIEW_PATH = "/v1/admin/data-plane/overview";
export const DPLANE_DBOPS_BASE = "/v1/admin/data-plane/db-ops";
export const DPLANE_MONITORING_SUMMARY_PATH = "/v1/admin/data-plane/monitoring/summary";
export const DPLANE_MONITORING_VPS_FLEET_PATH = "/v1/admin/data-plane/monitoring/vps-fleet";

/* --------------------------------------------------------------- result */

/**
 * Same discriminated union the cockpit fleet client uses
 * (`apps/admin/src/gtcc/api/fleet.ts:167`). `disabled` is the honest name
 * for a 404: db-ops has two independent server gates and *both* answer 404
 * rather than 403 (`db-ops/routes.ts:211` for the reads,
 * `db-ops/routes.ts:393` for the plan routes), so a 404 means "surface
 * switched off", not "request failed".
 */
export type DplaneResult<T> =
  | { readonly ok: true; readonly data: T }
  | { readonly ok: false; readonly kind: "disabled"; readonly httpStatus: 404 }
  | { readonly ok: false; readonly kind: "forbidden"; readonly httpStatus: 403 }
  | {
      readonly ok: false;
      readonly kind: "upstream";
      readonly httpStatus: number;
      readonly requestId: string | undefined;
    }
  | { readonly ok: false; readonly kind: "transport_error"; readonly message: string };

/** Wraps one existing read fetcher; never swallows, always classifies. */
export async function readDplane<T>(load: () => Promise<T>): Promise<DplaneResult<T>> {
  try {
    return { ok: true, data: await load() };
  } catch (err) {
    if (err instanceof ApiError) {
      const { status, requestId } = err;
      if (status === 404) return { ok: false, kind: "disabled", httpStatus: 404 };
      if (status === 403) return { ok: false, kind: "forbidden", httpStatus: 403 };
      return { ok: false, kind: "upstream", httpStatus: status, requestId };
    }
    const message = err instanceof Error ? err.message : "gtcc dplane fetch failed";
    return { ok: false, kind: "transport_error", message };
  }
}

/* ------------------------------------------------- health center (reads) */

export function fetchDplaneOverview(
  signal?: AbortSignal,
): Promise<DplaneResult<DataPlaneHealthOverview>> {
  return readDplane(() => fetchDataPlaneOverview(signal));
}

/* ------------------------------------------------ control plane (reads) */

export function fetchDplaneDbOpsOverview(
  signal?: AbortSignal,
): Promise<DplaneResult<DbOpsOverview>> {
  return readDplane(() => fetchDbOpsOverview(signal));
}

export function fetchDplaneHosts(
  signal?: AbortSignal,
): Promise<DplaneResult<ReadonlyArray<DbOpsHost>>> {
  return readDplane(() => fetchDbOpsHosts(signal));
}

export function fetchDplaneInstances(
  signal?: AbortSignal,
): Promise<DplaneResult<ReadonlyArray<PostgresInstance>>> {
  return readDplane(() => fetchDbOpsPostgresInstances(signal));
}

export function fetchDplaneDatabases(
  signal?: AbortSignal,
): Promise<DplaneResult<ReadonlyArray<DbOpsDatabase>>> {
  return readDplane(() => fetchDbOpsDatabases(signal));
}

export function fetchDplaneAssignments(
  signal?: AbortSignal,
): Promise<DplaneResult<ReadonlyArray<TenantDatabaseAssignment>>> {
  return readDplane(() => fetchDbOpsTenantAssignments(signal));
}

/* -------------------------------------------------- plan history (reads) */

/** How many plans the history asks for; the server orders newest first. */
export const DPLANE_PLAN_LIMIT = 10;

export function fetchDplanePlans(
  signal?: AbortSignal,
): Promise<DplaneResult<ReadonlyArray<DbOpsPersistedPlan>>> {
  return readDplane(() => fetchDbOpsPersistedPlans({ limit: DPLANE_PLAN_LIMIT }, signal));
}

export function fetchDplanePlanTimeline(
  planId: string,
  signal?: AbortSignal,
): Promise<DplaneResult<ReadonlyArray<DbOpsPersistedAuditEvent>>> {
  return readDplane(() => fetchDbOpsPlanAuditEvents(planId, signal));
}

/* ------------------------------------------------ monitoring & alerts (DB) */

/**
 * The monitoring client already returns its own union; it is normalised
 * onto `DplaneResult` so all four tabs render failures through one gate.
 * `envelope` becomes `upstream` — the envelope body is sanitised upstream
 * data and is deliberately not surfaced in this view.
 */
export async function fetchDplaneMonitoringSummary(
  signal?: AbortSignal,
): Promise<DplaneResult<MonitoringSummaryDto>> {
  return normaliseMonitoring(await fetchMonitoringSummary(signal), (r) => r.summary);
}

export async function fetchDplaneVpsFleet(
  signal?: AbortSignal,
): Promise<DplaneResult<VpsFleetDto>> {
  return normaliseMonitoring(await fetchVpsFleet(signal), (r) => r.fleet);
}

function normaliseMonitoring<R extends MonitoringSummaryResult | VpsFleetResult, T>(
  result: R,
  pick: (ok: Extract<R, { ok: true }>) => T,
): DplaneResult<T> {
  if (result.ok) return { ok: true, data: pick(result as Extract<R, { ok: true }>) };
  if (result.kind === "disabled") return { ok: false, kind: "disabled", httpStatus: 404 };
  if (result.kind === "forbidden") return { ok: false, kind: "forbidden", httpStatus: 403 };
  if (result.kind === "transport_error") {
    return { ok: false, kind: "transport_error", message: result.message };
  }
  return {
    ok: false,
    kind: "upstream",
    httpStatus: result.httpStatus,
    requestId: result.requestId,
  };
}

/* ------------------------------------------------ plan builders (dry run) */

/**
 * The three [Validate] actions of the Plan Builders tab. They are the only
 * POSTs in this view, they run only on an explicit click, and the server
 * answers them with `mode: "dry_run"` — nothing is executed. There is no
 * execute control in this view at all.
 */
export function validateDplaneCreateDatabasePlan(
  body: CreateDatabasePlanRequest,
  signal?: AbortSignal,
): Promise<DplaneResult<DbOpsPersistedPlan>> {
  return readDplane(async () => (await submitCreateDatabasePlan(body, signal)).plan);
}

export function validateDplaneAssignmentPlan(
  body: AssignmentPlanRequest,
  signal?: AbortSignal,
): Promise<DplaneResult<DbOpsPersistedPlan>> {
  return readDplane(async () => (await submitAssignmentPlan(body, signal)).plan);
}

export function validateDplaneMovePlan(
  body: MovePlanRequest,
  signal?: AbortSignal,
): Promise<DplaneResult<DbOpsPersistedPlan>> {
  return readDplane(async () => (await submitMovePlan(body, signal)).plan);
}

export type {
  AssignmentPlanRequest,
  CreateDatabasePlanRequest,
  DbOpsPersistedAuditEvent,
  DbOpsPersistedPlan,
  MovePlanRequest,
};

/* ----------------------------------------------------------- query keys */

/*
 * Prefix convention of the GTCC query-key inventory: `["gtcc", <viewkey>, …]`.
 * Every key is its own exported array literal — A-GTCC-QUERY-KEY-CONSISTENCY1
 * reads the keys statically out of the source and can follow
 * `queryKey: DPLANE_OVERVIEW_KEY` to this literal, while a member access
 * would stay unreadable for it.
 *
 * The registry points BOTH `dplane` and `dbops` at this view (P19 merges the
 * old DB Health Center and the old DB-Ops console), so both registry entries
 * declare the single prefix `["gtcc", "dplane"]`.
 */
export const DPLANE_OVERVIEW_KEY = ["gtcc", "dplane", "overview"] as const;
export const DPLANE_DBOPS_OVERVIEW_KEY = ["gtcc", "dplane", "dbops-overview"] as const;
export const DPLANE_HOSTS_KEY = ["gtcc", "dplane", "hosts"] as const;
export const DPLANE_INSTANCES_KEY = ["gtcc", "dplane", "instances"] as const;
export const DPLANE_DATABASES_KEY = ["gtcc", "dplane", "databases"] as const;
export const DPLANE_ASSIGNMENTS_KEY = ["gtcc", "dplane", "assignments"] as const;
export const DPLANE_PLANS_KEY = ["gtcc", "dplane", "plans"] as const;
export const DPLANE_PLAN_TIMELINE_KEY = ["gtcc", "dplane", "plan-timeline"] as const;
export const DPLANE_MONITORING_SUMMARY_KEY = ["gtcc", "dplane", "monitoring-summary"] as const;
export const DPLANE_VPS_FLEET_KEY = ["gtcc", "dplane", "vps-fleet"] as const;

export const DPLANE_QUERY_KEYS = {
  overview: DPLANE_OVERVIEW_KEY,
  dbOpsOverview: DPLANE_DBOPS_OVERVIEW_KEY,
  hosts: DPLANE_HOSTS_KEY,
  instances: DPLANE_INSTANCES_KEY,
  databases: DPLANE_DATABASES_KEY,
  assignments: DPLANE_ASSIGNMENTS_KEY,
  plans: DPLANE_PLANS_KEY,
  planTimeline: (planId: string) => [...DPLANE_PLAN_TIMELINE_KEY, planId] as const,
  monitoringSummary: DPLANE_MONITORING_SUMMARY_KEY,
  vpsFleet: DPLANE_VPS_FLEET_KEY,
} as const;
